<template>
    <section>
        <div v-if="!open && status === 'uncompleted'"
             class="buyflow-panel bg-gray-200">
            <div class="font-bold text-lg text-gray-600">
                Schedule Delivery/Installation
            </div>
        </div>
        <div v-else class="buyflow-panel space-y-6">
            <CheckoutCardBar
                :open="open"
                :status="status"
                :heading="getText('heading')"
                :description="getText('subheading')"
                :can-edit="!(hasSelectedSelfInstall && !isTechDelivered)"
                @edit="edit"
                @cancel-edit="cancelEdit"
            />
            <div v-if="!scheduleTimes.length && open || loadingDates">
                <div v-if="tryAgain" class="mb-2 flex items-center">
                    <IconCircleExclamation class="text-red-500 mr-2" />
                    Failed to load available dates
                    <a @click="retry()" class="underline font-bold ml-2">Try again?</a>
                </div>
                <div class="bg-white desktop:bg-gray-300 desktop:p-6 rounded-md animate-pulse">
                    <div class="widescreen:w-202 flex flex-col justify-between gap-y-4 mx-auto">
                        <div class="grid grid-cols-1 desktop:grid-cols-3 gap-4">
                            <div v-for="i in Array(3)" :key="i" class="bg-white p-4 rounded shadow-md desktop:shadow-sm">
                                <div class="rounded-full bg-gray-400 h-6 mx-5 "></div>
                                <hr class="border-gray-light my-3">
                                <div class="rounded-full bg-gray-400 h-4 mx-2 mb-2.5"></div>
                                <div class="rounded-full bg-gray-400 h-4 mx-2 mb-2.5"></div>
                                <div class="rounded-full bg-gray-400 h-4 mx-2 mb-2.5"></div>
                                <div class="rounded-full bg-gray-400 h-4 mx-2 "></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else-if="scheduleTimes.length && open">
                <div class=" bg-white desktop:bg-blue-lightest desktop:p-6 rounded-md">
                    <div class="widescreen:w-202 flex flex-col justify-between gap-y-4 mx-auto">
                        <div class="grid grid-cols-1 desktop:grid-cols-3 gap-4">
                            <div v-for="(item, index) in paginatedDates" :key="index"
                                 class="bg-white p-4 rounded shadow-md desktop:shadow-sm text-sm widescreen:text-base">
                                <div class="text-center">
                                    <strong>{{ item.formattedDate }}</strong>
                                </div>
                                <hr class="border-gray-light my-2" />
                                <div v-for="(time, key) in item.timeSlots"
                                    :key="key"
                                    class="mb-1">
                                    <label class="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            :id="'timeframe_' + key"
                                            :name="'selected-schedule'"
                                            data-cy="input-timeslot"
                                            :value="{ date: item.date, time: time }"
                                            v-model="selectedDateTime">
                                        <span class="ml-2">{{ formatInstallTime(time) }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div v-if="timeslotDisclaimer" class="mt-6 ml-1 text-xs font-bold">
                            {{ timeslotDisclaimer }}
                        </div>

                        <div
                            v-if="hasMorePages || previousPageText"
                            :class="isSingleElementClass">
                            <button v-if="previousPageText" type="button" class="font-bold underline" @click="previousPage">
                                {{ previousPageText }}
                            </button>
                            <button v-if="hasMorePages" type="button" class="font-bold underline" @click="nextPage">
                                {{ nextPageText }}
                            </button>
                           <button v-else-if="hasAvailableDates.length <= 45" type="button" class="font-bold underline" @click="fetchDates()">
                               Load More Dates
                           </button>
                        </div>
                    </div>
                </div>

                <button type="button"
                        class="buyflow-button mt-4 w-full desktop:w-auto flex items-center text-sm widescreen:text-base"
                        :disabled="!selectedDateTime"
                        @click="confirmInstallation()"
                        data-cy="button-confirm-timeslot">
                    <IconCircleNotch class="animate-spin text-white mr-2 text-lg" v-if="isLoading" />
                    Confirm Installation Time
                </button>
            </div>
        </div>
    </section>
</template>

<script>
    import {DateTime as dt, DateTime} from "luxon"
    import IconCheck from "@/atoms/icons/IconCheck"
    import AppModal from "~/Buyflow/components/AppModal"
    import {mapGetters, mapState, mapActions} from "vuex"
    import IconCircleNotch from "@/atoms/icons/IconCircleNotch"
    import CheckoutCardBar from "../CheckoutCardBar/index.vue";
    import IconCircleExclamation from "../../../../components/atoms/icons/IconCircleExclamation.vue";

    export default {
        name: 'CheckoutScheduler',
        components: {
            IconCircleExclamation,
            AppModal,
            IconCheck,
            IconCircleNotch,
            CheckoutCardBar,
        },
        props: {
            open: {
                type: Boolean,
                default: false,
            },
            status: {
                type: String,
                default: 'uncompleted',
            },
            scheduleTimes: {
                type: Array,
                required: true,
            },
            hasSelectedSelfInstall: {
                type: Boolean,
                required: true,
            },
            isSelfInstallEligible: {
                type: Boolean,
                default: false,
            },
            isTechDelivered: {
                type: Boolean,
                required: false,
            },
            uuid: {
                type: String,
                default: '',
            },
            conversationId: {
                type: String,
                default: '',
            },
            submitScheduleInstall: {
                type: Function,
                default: () => {},
            },
            confirmedDateTime: {
                type: Object,
                default: null,
            },
        },
        data: () => ({
            currentPage: 1,
            tryAgain: false,
            isLoading: false,
            loadingDates: false,
            tryAgainTimeout: null,
            selectedDateTime: null,
        }),
        computed: {
            ...mapState({
                installIsScheduled: state => state.Buyflow.finalize.installIsScheduled
            }),
            ...mapGetters({
                formatInstallDate: 'Buyflow/finalize/formatInstallDate',
                formatInstallTime: 'Buyflow/finalize/formatInstallTime',
            }),
            paginatedDates() {
                const startIndex = (this.currentPage - 1) * 3
                const endIndex = startIndex + 3
                return this.hasAvailableDates
                    .slice(startIndex, endIndex)
                    .map((value) => ({
                        ...value,
                        formattedDate: DateTime.fromFormat(value.date, "M/d/yyyy h:mm:ss a").toFormat("EEEE, MMM d"),
                    }))
            },
            hasMorePages() {
                const itemsPerPage = 3
                const totalPages = Math.ceil(this.hasAvailableDates.length / itemsPerPage)
                return this.currentPage < totalPages
            },
            hasAvailableDates() {
                return this.scheduleTimes ?? []
            },
            timeslotDisclaimer(){
                return this.hasSelectedSelfInstall ? "*Our delivery method(s) above do not require anyone to be home at the time of delivery.": ''
            },
            nextPageText() {
                return this.currentPage === 1 ? 'View More Available Dates' : 'Next'
            },
            previousPageText() {
                return this.currentPage !== 1 ? 'Previous' : ''
            },
            isSingleElement() {
                return !this.hasMorePages || !this.previousPageText
            },
            isSingleElementClass() {
                return ['flex', this.isSingleElement && this.nextPageText === 'View More Available Dates' ? 'justify-center desktop:justify-end' : 'justify-between', 'items-center', 'w-full']
            }
        },
        watch: {
            selectedDateTime(value) {
                if (value && value.hasOwnProperty("date") && value.hasOwnProperty("time")) {
                    this.selectedDateTime = value
                }
            },
            scheduleTimes(value) {
                if (value && value.length > 0) {
                    const first = value[0]
                    this.selectedDateTime = { date: first?.date, time: first?.timeSlots[0]}
                }
            },
            open(value) {
                if (!value){
                    clearTimeout(this.tryAgainTimeout)
                    this.tryAgain = false
                    return;
                }
                if (this.scheduleTimes.length === 0) {
                    this.getScheduledDates()
                }
            }
        },
        mounted () {
            this.sendEvent('resi: checkout', 'resi: checkout confirmation - professional delivery', 'resi: checkout confirmation page')
            this.$emit('update-next-disabled-state', true)
        },
        methods: {
            ...mapActions({
                togglePopup: 'togglePopup',
                toggleLoading: 'toggleLoading',
                fetchSchedulerTimes: 'Buyflow/finalize/fetchSchedulerTimes',
            }),
            retry(){
                this.tryAgain = false
                this.$nextTick(() => {
                    this.getScheduledDates();
                })
            },
            getScheduledDates() {
                this.fetchSchedulerTimes({ startingDate: dt.now().toFormat('yyyy-LL-dd') + 'T00:00:00.000Z'})
                    .finally(() => {
                        if (this.scheduleTimes.length === 0) {
                            this.tryAgain = true
                        }
                    })
            },
            getText(type){
                switch (type){
                    case 'heading':
                        if (this.confirmedDateTime && this.status === 'completed') {
                            return this.isTechDelivered || this.hasSelectedSelfInstall ? 'Your Install Kit is scheduled for delivery': 'Your professional install is scheduled'
                        }
                        return this.isTechDelivered || this.hasSelectedSelfInstall ? 'Delivery of Your Self Install Kit': 'Your service will be professionally installed!'
                    case 'subheading':
                        if (this.confirmedDateTime && this.status === 'completed') {
                            return this.formatInstallDate(this.confirmedDateTime?.date.split(' ')[0], this.confirmedDateTime?.time)
                        } else if (this.status === 'completed' && !this.open) {
                            return ''
                        }
                        return this.isTechDelivered || this.hasSelectedSelfInstall ? 'Please choose a date for your delivery:': 'Please choose an appointment date and time for your at home installation:'
                    default:
                        return ''
                }
            },
            edit() {
                this.$emit('set-finalize-state', {
                    open: 'scheduler',
                })
                this.$emit('edit-scheduled-time')
            },
            cancelEdit() {
                this.$emit('set-finalize-state', {
                    open: 'confirmation',
                })
            },
            confirmInstallation() {
                if (this.isLoading) return // prevent double click
                this.isLoading = true

                const selectedDate = this.selectedDateTime?.date
                const selectedTime = this.selectedDateTime?.time

                if (selectedDate !== null && selectedTime !== null ){
                    this.sendEvent('resi: checkout', `resi: checkout confirmation - select schedule time - ${selectedDate} ${selectedTime}`, 'resi: checkout confirmation page')
                    this.submitScheduleInstall({
                        date: DateTime.fromFormat(selectedDate.split(' ')[0], 'L/d/yyyy').toFormat('yyyy-LL-dd') + 'T00:00:00.000000Z',
                        timeSlot: selectedTime
                    }).then(() => {
                        this.$emit('set-confirmed-datetime', this.selectedDateTime)
                        this.isLoading = false
                        this.$emit('set-finalize-state', {
                            open: 'confirmation',
                            scheduler: 'completed',
                        })
                        // Clear confirm order when this is resubmitted
                        this.$emit('clear-confirmation')
                    }).catch(e => {
                        this.isLoading = false,
                        this.$store.dispatch('togglePopup', { 
                            type: 'COMMON/ERRORS/UNEXPECTED', 
                            error: e,
                        }, ROOT)
                    })
                }
            },
            nextPage() {
                this.currentPage += 1
            },
            previousPage() {
                this.currentPage -= 1
            },
            fetchDates() {
                const dayAfterLastAvailable = this.hasAvailableDates[this.hasAvailableDates?.length - 1]?.date

                // convert this 8/15/2023 12:00:00 AM to this 2023-08-15T00:00:00.000000Z
                const startingDate = DateTime.fromFormat(dayAfterLastAvailable?.split(' ')[0], 'L/d/yyyy').plus({days:1}).toFormat('yyyy-LL-dd') + 'T00:00:00.000Z'
                this.loadingDates = true

                // throw new Error('Error retrieving dates')
                this.fetchSchedulerTimes({startingDate})
                    .then(() => {
                        if (this.hasMorePages) {
                            this.nextPage()
                        }
                    }).finally(() => {
                        this.loadingDates = false
                    })
            },
        },
    }
</script>
